// ─── Assistant WhatsApp — numbers & pairing (2026-08-12) ────────────────────
// Pair and manage the WhatsApp numbers the 24/7 assistant answers on.
//
// HOW THIS PAGE TALKS TO WHATSAPP: it doesn't. The gateway runs on a VPS bound
// to localhost with no public port — that is what keeps the WhatsApp session
// off the internet. This page writes what it WANTS into the database; the relay
// on the VPS reads that, acts, and writes back what is actually TRUE.
//
// Consequence for the UI: every status here is a REPORT, not a live reading. If
// the relay is offline the numbers are unknown, not fine — hence the banner at
// the top, which is deliberately impossible to miss.
//
// DESIGN NOTES (ui-ux-pro-max pass):
//  · SVG icons only — emoji render differently per platform and can't be themed.
//  · Status is never colour alone: every state carries an icon AND a word, so it
//    survives colour-blindness and greyscale.
//  · Light theme on purpose. The design system suggested dark for an ops
//    dashboard, but the rest of Storiz admin is light and mixing the two reads
//    as a bolted-on page.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/Button';
import {
  whatsappApi,
  type WhatsAppNumber,
  type WhatsAppRelayHealth,
  type WhatsAppOrphanSession,
} from '@/lib/api';
import { useT } from '@/lib/i18n';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { BrainTab } from './assistant/BrainTab';
import { ConversationsTab } from './assistant/ConversationsTab';
import { SimulatorTab } from './assistant/SimulatorTab';
import { SalesTab } from './assistant/SalesTab';
import { FollowUpsTab } from './assistant/FollowUpsTab';
import { ProcessTab } from './assistant/process/ProcessTab';
import { EngineCard } from './assistant/EngineCard';
import { TestNumbersCard } from './assistant/TestNumbersCard';
import { DepartmentContactsCard } from './assistant/DepartmentContactsCard';
import { InboxTab } from './assistant/InboxTab';
import { PipelineTab } from './assistant/PipelineTab';
import { LiveTab } from './assistant/LiveTab';
import { ApprovalsTab } from './assistant/ApprovalsTab';
import { EditShadowTab } from './assistant/EditShadowTab';
import { AgentStatusPill } from './assistant/AgentStatusPill';
import { MasterSwitch } from './assistant/MasterSwitch';
import { usePilotage } from './assistant/usePilotage';
import { groupByClient, latestIso, splitByPeriod } from './assistant/workbench';
import { DateRangePicker, presetRange, type DateRange } from '@/components/admin/DateRangePicker';
import { ManualReminders } from './AdminFollowUpPage';
import { NumbersStrip } from './assistant/NumbersStrip';
import { SwipeStrip } from './assistant/SwipeStrip';
import { useIsPhone } from './assistant/phone';
import { CHAT_FROM_LIST, closeChatMove, tabParams, withChat, withoutChat } from './assistant/assistantUrl';
import type { StripTarget } from './assistant/numbers';

const inputCls =
  'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white transition-colors duration-200 ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent';

/** While a number is pairing we poll fast — a QR only lives ~20s, so a slow
 *  refresh would show an expired code. Otherwise poll gently. */
const PAIRING_POLL_MS = 3_000;
const IDLE_POLL_MS = 20_000;

const PAIRING_STATUSES = new Set(['pending', 'creating', 'qr_ready', 'connecting']);

// ── Icons (inline SVG, 1.7 stroke to match AdminLayout's nav icons) ──────────
type IconProps = { className?: string };
const Icon = ({ children, className = 'w-[18px] h-[18px]' }: IconProps & { children: React.ReactNode }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
    strokeLinecap="round" strokeLinejoin="round" className={`${className} shrink-0`} aria-hidden="true">
    {children}
  </svg>
);
const CheckIcon = (p: IconProps) => <Icon {...p}><path d="M20 6 9 17l-5-5" /></Icon>;
const AlertIcon = (p: IconProps) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16.5v.01" /></Icon>;
const ClockIcon = (p: IconProps) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>;
const PlugOffIcon = (p: IconProps) => <Icon {...p}><path d="M3 3l18 18M9 7V3M15 7V3M7 7h10v4a5 5 0 0 1-5 5 5 5 0 0 1-5-5V7zM12 16v5" /></Icon>;
const QuestionIcon = (p: IconProps) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3 2.45V13M12 16.5v.01" /></Icon>;
const PhoneIcon = (p: IconProps) => <Icon {...p}><rect x="6" y="2" width="12" height="20" rx="2.5" /><path d="M11 18.5h2" /></Icon>;
const QrIcon = (p: IconProps) => (
  <Icon {...p}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" /><path d="M14 14h3v3h-3zM20 14v.01M14 20v.01M20 20v.01M17.5 20v.01M20 17.5v.01" /></Icon>
);
const KeypadIcon = (p: IconProps) => (
  <Icon {...p}><circle cx="6" cy="6" r="1.4" /><circle cx="12" cy="6" r="1.4" /><circle cx="18" cy="6" r="1.4" />
    <circle cx="6" cy="12" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="18" cy="12" r="1.4" />
    <circle cx="12" cy="18" r="1.4" /></Icon>
);
const PlusIcon = (p: IconProps) => <Icon {...p}><path d="M12 5v14M5 12h14" /></Icon>;

// ── Status vocabulary ────────────────────────────────────────────────────────
// Icon + word + colour, never colour alone. `tone` drives the tile counters too.
type StatusKey = WhatsAppNumber['status'];
function statusMeta(status: StatusKey, t: (s: string) => string) {
  // Classes, not inline colours: each light pair keeps today's exact values and
  // carries its night partner (status tokens — red stuck, green done, orange waiting).
  const ok = 'bg-[#DCFCE7] text-status-done dark:bg-status-done-bg';
  const wait = 'bg-[#FEF3C7] text-status-waiting dark:bg-status-waiting-bg';
  const idle = 'bg-[#F3F4F6] text-[#4B5563] dark:bg-adm-surface-3 dark:text-adm-ink-2';
  const bad = 'bg-[#FEE2E2] text-status-stuck dark:bg-status-stuck-bg';
  const map: Record<StatusKey, { label: string; cls: string; Icon: (p: IconProps) => React.ReactElement }> = {
    connected: { label: t('connecté'), cls: ok, Icon: CheckIcon },
    connecting: { label: t('connexion…'), cls: wait, Icon: ClockIcon },
    qr_ready: { label: t('à scanner'), cls: wait, Icon: QrIcon },
    creating: { label: t('préparation…'), cls: idle, Icon: ClockIcon },
    pending: { label: t('en attente'), cls: idle, Icon: ClockIcon },
    disconnected: { label: t('déconnecté'), cls: idle, Icon: PlugOffIcon },
    failed: { label: t('échec'), cls: bad, Icon: AlertIcon },
  };
  return map[status] ?? map.pending;
}

function StatusPill({ status, relayOnline }: { status: StatusKey; relayOnline: boolean }) {
  const { t } = useT();
  // A stale report must never be painted green. When the relay is down the
  // honest answer is "we don't know", not the last thing it told us.
  if (!relayOnline) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-gray-100 text-gray-600">
        <QuestionIcon className="w-3.5 h-3.5" />
        {t('inconnu')}
      </span>
    );
  }
  const s = statusMeta(status, t);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${s.cls}`}>
      <s.Icon className="w-3.5 h-3.5" />
      {s.label}
    </span>
  );
}

// ── Stat tiles ───────────────────────────────────────────────────────────────
function StatTile({ label, value, tone }: { label: string; value: number | string; tone: 'ok' | 'warn' | 'neutral' }) {
  // = #15803D / #B45309 / #111827 in light, their night values in dark.
  const fg = tone === 'ok' ? 'var(--color-status-done)' : tone === 'warn' ? 'var(--color-status-waiting)' : 'var(--color-adm-ink)';
  return (
    <div className="bg-white rounded-xl border border-gray-200 px-4 py-3.5">
      <p className="text-[10.5px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
      {/* tabular-nums: counters must not jiggle in width as they change */}
      <p className="text-[26px] leading-tight font-bold tabular-nums mt-0.5" style={{ color: fg }}>{value}</p>
    </div>
  );
}

function RelayBanner({ relay }: { relay: WhatsAppRelayHealth }) {
  const { t } = useT();
  // Healthy = silent (owner, 2026-10-08: « make the chat bigger »): the bar
  // took a whole row to say nothing was wrong. It shows only when something
  // IS wrong (below); the agent pill keeps the quiet « ok ».
  if (relay.online) return null;
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-2.5" role="status">
      <PlugOffIcon className="w-[18px] h-[18px] text-red-700 mt-px" />
      <div>
        <p className="text-[12.5px] font-semibold text-red-800">{t('Le serveur de l’assistant ne répond pas.')}</p>
        <p className="text-[12px] text-red-700 mt-0.5 leading-snug">
          {t('Les statuts ci-dessous datent de sa dernière connexion — ils ne sont plus fiables. Aucun message n’est envoyé ni reçu tant qu’il est hors ligne.')}
        </p>
        {relay.seen_at && (
          <p className="mt-1 text-[11.5px] text-red-600">
            {t('Dernier signe de vie :')} {new Date(relay.seen_at).toLocaleString('fr-FR')}
          </p>
        )}
      </div>
    </div>
  );
}

/** The pairing panel: QR to scan, or an 8-character code to type. */
function PairingPanel({ number, onDone }: { number: WhatsAppNumber; onDone: () => void }) {
  const { t } = useT();
  const [phone, setPhone] = useState(number.pairing_phone ?? '');
  const codeMut = useMutation({ mutationFn: () => whatsappApi.pairingCode(number.id, phone), onSuccess: onDone });

  return (
    <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* ── Option 1: QR ── */}
      <div>
        <div className="flex items-center gap-2 mb-2.5">
          <span className="w-5 h-5 rounded-full bg-gray-900 text-white text-[11px] font-bold flex items-center justify-center tabular-nums">1</span>
          <p className="text-[12px] font-semibold text-gray-700">{t('Scanner le QR code')}</p>
        </div>
        {number.qr_data_url ? (
          <>
            {/* White padding around the code: WhatsApp's scanner needs the quiet
                zone, and a QR flush to a border reads badly on some screens. */}
            <div className="w-[196px] h-[196px] rounded-xl border border-gray-200 bg-white p-2.5">
              <img src={number.qr_data_url} alt={t('QR code à scanner avec WhatsApp')} className="w-full h-full" />
            </div>
            <p className="text-[11.5px] text-gray-500 mt-2.5 leading-snug">
              {t('WhatsApp → Appareils connectés → Connecter un appareil.')}
            </p>
            {/* Saying the code rotates prevents the "it doesn't work" loop where
                someone scans a code that expired while they were looking at it. */}
            <p className="text-[11px] text-amber-700 mt-1 leading-snug">
              {t('Le code change toutes les 20 secondes — s’il échoue, attendez le suivant.')}
            </p>
          </>
        ) : (
          <div className="w-[196px] h-[196px] rounded-xl border border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center gap-2 px-4 text-center">
            <QrIcon className="w-7 h-7 text-gray-300" />
            <span className="text-[11.5px] text-gray-400">
              {number.qr_stale ? t('Nouveau code en préparation…') : t('En attente du serveur…')}
            </span>
          </div>
        )}
      </div>

      {/* ── Option 2: pairing code ── */}
      <div>
        <div className="flex items-center gap-2 mb-2.5">
          <span className="w-5 h-5 rounded-full bg-gray-900 text-white text-[11px] font-bold flex items-center justify-center tabular-nums">2</span>
          <p className="text-[12px] font-semibold text-gray-700">{t('Ou recevoir un code à taper')}</p>
        </div>
        <p className="text-[11.5px] text-gray-500 mb-2.5 leading-snug">
          {t('Plus fiable que le QR : le code reste valable plus longtemps.')}
        </p>

        <label htmlFor={`phone-${number.id}`} className="block text-[11px] font-semibold text-gray-500 mb-1">
          {t('Numéro du téléphone')}
        </label>
        <div className="flex gap-2">
          <input
            id={`phone-${number.id}`} className={inputCls} value={phone}
            onChange={(e) => setPhone(e.target.value)} placeholder="212600000000"
            inputMode="numeric" autoComplete="tel"
          />
          <Button size="sm" onClick={() => codeMut.mutate()} loading={codeMut.isPending} disabled={phone.trim().length < 8}>
            {t('Obtenir')}
          </Button>
        </div>
        {/* Helper text is persistent, not a placeholder that vanishes on focus. */}
        <p className="text-[11px] text-gray-400 mt-1">{t('Avec l’indicatif, sans + ni espaces.')}</p>

        {number.pairing_code && (
          <div className="mt-3 rounded-xl bg-brand-50 border border-brand-200 px-4 py-3">
            <p className="text-[10.5px] font-semibold text-brand-700 uppercase tracking-wide flex items-center gap-1.5">
              <KeypadIcon className="w-3.5 h-3.5" /> {t('Code')}
            </p>
            <p className="text-[24px] font-bold tracking-[0.2em] text-brand-800 tabular-nums mt-0.5">{number.pairing_code}</p>
            <p className="text-[11.5px] text-brand-700 mt-1.5 leading-snug">
              {t('Sur le téléphone : WhatsApp → Appareils connectés → Connecter un appareil → Associer avec le numéro de téléphone.')}
            </p>
          </div>
        )}
        {codeMut.isError && (
          <p className="text-[11.5px] text-red-600 mt-2" role="alert">{(codeMut.error as Error).message}</p>
        )}
      </div>
    </div>
  );
}

const actionBtn =
  'text-[11.5px] font-medium underline underline-offset-2 cursor-pointer rounded px-1.5 py-1 ' +
  'transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500';

function NumberCard({ number, relayOnline }: { number: WhatsAppNumber; relayOnline: boolean }) {
  const { t } = useT();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['whatsapp-numbers'] });

  const disconnectMut = useMutation({ mutationFn: () => whatsappApi.disconnect(number.id), onSuccess: invalidate });
  const reconnectMut = useMutation({ mutationFn: () => whatsappApi.reconnect(number.id), onSuccess: invalidate });
  const removeMut = useMutation({ mutationFn: () => whatsappApi.remove(number.id), onSuccess: invalidate });

  const pairing = PAIRING_STATUSES.has(number.status) && number.desired_state === 'connected';
  const removing = number.desired_state === 'deleted';
  const busy = disconnectMut.isPending || reconnectMut.isPending || removeMut.isPending;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 transition-shadow duration-200 hover:shadow-sm">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-9 h-9 rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
          <PhoneIcon className="w-[18px] h-[18px] text-gray-400" />
        </div>
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold text-gray-800 truncate">{number.label}</p>
          <p className="text-[11.5px] text-gray-500 truncate tabular-nums">
            {number.phone ? number.phone : <span className="text-gray-400">{t('numéro pas encore associé')}</span>}
          </p>
        </div>
        <div className="flex-1" />
        <StatusPill status={number.status} relayOnline={relayOnline} />

        {removing ? (
          <span className="text-[11.5px] text-gray-400">{t('suppression en cours…')}</span>
        ) : (
          <div className="flex items-center gap-1">
            {number.status === 'connected' ? (
              <button className={`${actionBtn} text-gray-600 hover:text-gray-900`} disabled={busy}
                onClick={() => disconnectMut.mutate()}>
                {t('Déconnecter')}
              </button>
            ) : (
              <button className={`${actionBtn} text-brand-600 hover:text-brand-800`} disabled={busy}
                onClick={() => reconnectMut.mutate()}>
                {t('Reconnecter')}
              </button>
            )}
            {/* Destructive action sits apart and is the only red thing here. */}
            <button className={`${actionBtn} text-red-600 hover:text-red-800 ml-1`} disabled={busy}
              onClick={() => {
                // Unpairing can't be undone from here — the phone must scan again.
                if (window.confirm(t('Supprimer ce numéro ? Il sera déconnecté de WhatsApp et devra être ré-associé.'))) {
                  removeMut.mutate();
                }
              }}>
              {t('Supprimer')}
            </button>
          </div>
        )}
      </div>

      {number.last_error && (
        <p className="mt-2.5 text-[11.5px] text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2 flex items-start gap-2" role="alert">
          <AlertIcon className="w-4 h-4 mt-px shrink-0" />
          <span>{number.last_error}</span>
        </p>
      )}

      {pairing && <PairingPanel number={number} onDone={invalidate} />}
    </div>
  );
}

/**
 * Sessions running on the gateway that Storiz doesn't own yet.
 *
 * These appear when someone creates a session directly in the OpenWA dashboard.
 * Without this card they are invisible here — a live WhatsApp connection that
 * nobody in Storiz can see, pause or delete. Importing binds the existing
 * session to a Storiz number instead of starting a second one beside it.
 */
function OrphanCard({ orphans }: { orphans: WhatsAppOrphanSession[] }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [labels, setLabels] = useState<Record<string, string>>({});

  const adoptMut = useMutation({
    mutationFn: ({ id, label }: { id: string; label: string }) => whatsappApi.adopt(id, label),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp-numbers'] }),
  });

  if (!orphans.length) return null;

  return (
    <div className="bg-white rounded-xl border border-amber-200 p-5">
      <h2 className="text-sm font-semibold text-gray-800 mb-1">{t('Sessions détectées sur le serveur')}</h2>
      <p className="text-[11.5px] text-gray-500 mb-3 leading-snug">
        {t('Ces sessions WhatsApp tournent sur le serveur mais ne sont pas encore gérées ici. Importez-les pour les voir et les contrôler depuis Storiz.')}
      </p>
      <div className="space-y-2.5">
        {orphans.map((o) => (
          <div key={o.id} className="flex items-end gap-2 flex-wrap border border-gray-100 rounded-lg p-3">
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] font-medium text-gray-800 truncate">{o.name}</p>
              <p className="text-[11px] text-gray-400 truncate tabular-nums">
                {o.phone ? o.phone : o.status}
              </p>
            </div>
            <div className="min-w-[190px]">
              <label htmlFor={`adopt-${o.id}`} className="block text-[11px] font-semibold text-gray-500 mb-1">
                {t('Nom à donner')}
              </label>
              <input
                id={`adopt-${o.id}`} className={inputCls}
                // Pre-filled with the gateway's own session name so the common
                // case is one click, but still editable.
                value={labels[o.id] ?? o.name}
                onChange={(e) => setLabels((s) => ({ ...s, [o.id]: e.target.value }))}
                maxLength={60}
              />
            </div>
            <Button
              size="sm"
              loading={adoptMut.isPending && adoptMut.variables?.id === o.id}
              onClick={() => adoptMut.mutate({ id: o.id, label: (labels[o.id] ?? o.name).trim() })}
            >
              {t('Importer')}
            </Button>
          </div>
        ))}
      </div>
      {adoptMut.isError && (
        <p className="text-[11.5px] text-red-600 mt-2" role="alert">{(adoptMut.error as Error).message}</p>
      )}
    </div>
  );
}

function SkeletonCard() {
  // Reserves the real card's height so the list doesn't jump when data lands.
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gray-100" />
        <div className="flex-1 space-y-2">
          <div className="h-3 bg-gray-100 rounded w-40" />
          <div className="h-2.5 bg-gray-50 rounded w-28" />
        </div>
        <div className="h-6 w-24 bg-gray-100 rounded-full" />
      </div>
    </div>
  );
}

// ─── THE SEVEN TABS (owner-approved plan, Part B, 2026-09-25) ────────────────
//
// Inbox · Pipeline · Conversations · Live · Brain · Automations · Settings.
// The tab lives in the URL (`?tab=`, and `&chat=` for a conversation), so a
// link in a note or an alert opens the right place, and Back works.
// + « Validations » (agent v2 `actions`, 2026-10-30): what the agent prepared and may not do alone.
type Tab = 'inbox' | 'pipeline' | 'conversations' | 'live' | 'approvals' | 'shadow' | 'brain' | 'automations' | 'settings';
const TABS: Tab[] = ['inbox', 'pipeline', 'conversations', 'live', 'approvals', 'shadow', 'brain', 'automations', 'settings'];
/** The old tab names still land somewhere sensible — bookmarks, links in notes. */
const LEGACY_TAB: Record<string, [Tab, string?]> = {
  pilotage: ['inbox'], numbers: ['settings'], engine: ['settings'],
  followups: ['automations', 'followups'], sales: ['automations', 'sales'], simulator: ['brain', 'simulator'],
};
function readTab(raw: string | null, rawSub: string | null): { tab: Tab; sub: string | null } {
  const v = String(raw ?? '');
  if ((TABS as string[]).includes(v)) return { tab: v as Tab, sub: rawSub };
  const legacy = LEGACY_TAB[v];
  return legacy ? { tab: legacy[0], sub: legacy[1] ?? rawSub } : { tab: 'inbox', sub: rawSub };
}

/** Two views under one tab — a segmented control, not another tab row. */
function SubSwitch({ value, options, onChange }: {
  value: string; options: Array<[string, string]>; onChange: (v: string) => void;
}) {
  return (
    <div role="tablist" className="inline-flex rounded-lg border border-border-subtle bg-bg-card p-0.5">
      {options.map(([k, label]) => (
        <button
          key={k} type="button" role="tab" aria-selected={value === k} onClick={() => onChange(k)}
          className={`px-3 h-8 rounded-md text-[12.5px] cursor-pointer transition-colors duration-150 ${
            value === k ? 'bg-accent-soft text-text-primary font-semibold' : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function AdminAssistantPage() {
  const { t } = useT();
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  // Opens on the Inbox — the manager's home: what needs a person, ranked.
  const [params, setParams] = useSearchParams();
  const { tab, sub } = readTab(params.get('tab'), params.get('sub'));
  const chatParam = params.get('chat');
  const go = (next: Tab, extra: Record<string, string> = {}) => setParams(tabParams(next, extra));
  const isPhone = useIsPhone();
  const location = useLocation();
  const navigate = useNavigate();
  // ⚠️ AN OPEN CHAT IS A HISTORY ENTRY (app-feel plan, builder B). Opened from
  // inside the page — the Inbox, the Pipeline, Live, the list on a phone — it
  // is PUSHED with `chatFromList`, so the back arrow, Back and the shell's
  // swipe-back all return to where it was opened. See assistantUrl.ts.
  const openChat = (chatId: string) => setParams(tabParams('conversations', { chat: chatId }), { state: CHAT_FROM_LIST });
  /** The Conversations list: a phone pushes (Back returns to the list); a desktop swaps in place, as before. */
  const selectChat = (chatId: string, opts?: { replace?: boolean }) => (isPhone
    // ← → / a swipe between chats (2026-10-30) swaps in place and keeps how the
    // chat was opened, so Back still returns to the list, not to each chat seen.
    ? (opts?.replace
      ? setParams(withChat(params, chatId), { replace: true, state: location.state })
      : setParams(withChat(params, chatId), { state: CHAT_FROM_LIST }))
    : setParams(withChat(params, chatId), { replace: true }));
  const closeChat = () => {
    if (closeChatMove(location.state) === 'pop') navigate(-1);
    else setParams(withoutChat(params), { replace: true });
  };
  const goStrip = (to: StripTarget) => go(to.tab, to.sub ? { sub: to.sub } : {});
  const snapshot = usePilotage();
  // ⚠️ THE TIMELINE (owner, 2026-09-26: « the default is 7 days »). The main
  // dashboard's own picker; Inbox, Pipeline and Conversations show what was
  // ACTIVE in the period and say how much they hide (workbench.ts splitByPeriod).
  const [range, setRange] = useState<DateRange>(() => presetRange('7d'));
  const showAll = () => setRange(presetRange('all'));
  // The badge counts what the Inbox shows: CLIENTS needing you now, in the period.
  const needsNow = useMemo(() => splitByPeriod(groupByClient(snapshot.data?.inbox?.items ?? []),
    (r) => latestIso(r.items.map((i) => i.activityIso)), range).shown.filter((r) => r.tier === 'now').length, [snapshot.data, range]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['whatsapp-numbers'],
    queryFn: () => whatsappApi.numbers(),
    // Poll fast only while something is actually being paired — a QR that
    // refreshes every 20s is useless if the page reloads every 20s too.
    refetchInterval: (q) => {
      const d = q.state.data;
      const pairing = d?.numbers?.some((n) => PAIRING_STATUSES.has(n.status) && n.desired_state === 'connected');
      return pairing ? PAIRING_POLL_MS : IDLE_POLL_MS;
    },
  });

  const addMut = useMutation({
    mutationFn: () => whatsappApi.addNumber(label.trim()),
    onSuccess: () => { setLabel(''); qc.invalidateQueries({ queryKey: ['whatsapp-numbers'] }); },
  });

  const numbers = data?.numbers ?? [];
  const orphans = data?.orphans ?? [];
  const relay = data?.relay ?? { online: false, seen_at: null, version: null };
  // Counters only mean anything if the relay is answering. Otherwise "2
  // connected" is a memory, not a fact.
  const connected = relay.online ? numbers.filter((n) => n.status === 'connected').length : 0;
  const waiting = relay.online ? numbers.filter((n) => PAIRING_STATUSES.has(n.status)).length : 0;

  return (
    <AdminLayout>
      {/* ⚠️ WIDTH FOLLOWS THE TAB. Boards and lists want the room; Settings is
          a form, and a form stretched to 1700px is harder to read, not easier. */}
      <div className={`${
        tab === 'settings' ? 'max-w-5xl' : 'max-w-[1700px]'
      } mx-auto md:px-6 md:py-6 space-y-4 transition-[max-width] duration-300`}>
        {/* ⚠️ NO PADDING ON A PHONE: AdminLayout already gives the 16 px gutter;
            this page used to add its own on top (app-feel plan, builder B). */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading text-[24px] leading-tight text-text-primary">{t('Assistant WhatsApp')}</h1>
            <p className="hidden md:block text-[12.5px] text-text-secondary mt-0.5">
              {t('Ce qui attend une personne, où en est chaque client, et l’agent au travail.')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* The period applies to the three lists; Live is « now » and the rest are settings. */}
            {(tab === 'inbox' || tab === 'pipeline' || tab === 'conversations') && (
              <DateRangePicker value={range} onChange={setRange} align="right" />
            )}
            {/* The agent's pulse, on every tab — nobody should open Live just to check it is alive. */}
            <AgentStatusPill live={snapshot.data?.live} onOpen={() => go('live')} />
          </div>
        </div>

        {/* Tabs. The relay banner sits ABOVE them on purpose: if the server is
            down, that fact applies to every tab, not just this one. */}
        <RelayBanner relay={relay} />

        {/* A swipeable strip on a phone (snap, edge fades, the active tab kept in
            view); the same row as before from md: up. Inbox first. */}
        <nav className="border-b border-border-subtle" aria-label={t('Sections de l’assistant')}>
          <SwipeStrip label={t('Sections de l’assistant')} activeKey={tab} className="gap-1">
          {([
            ['inbox', t('Inbox')],
            ['pipeline', t('Pipeline')],
            ['conversations', t('Conversations')],
            ['live', t('En direct')],
            ['approvals', t('Validations')],
            ['shadow', t('Retouches (essai)')],
            ['brain', t('Cerveau')],
            ['automations', t('Automatisations')],
            ['settings', t('Réglages')],
          ] as [Tab, string][]).map(([key, lbl]) => (
            <button
              key={key}
              type="button"
              data-strip-key={key}
              onClick={() => go(key)}
              aria-current={tab === key ? 'page' : undefined}
              className={`snap-start shrink-0 min-h-[44px] md:min-h-0 px-3.5 py-2 text-[13px] font-semibold cursor-pointer whitespace-nowrap transition-colors duration-200 border-b-2 -mb-px inline-flex items-center gap-1.5 ${
                tab === key
                  ? 'border-brand-500 text-text-primary'
                  : 'border-transparent text-text-tertiary hover:text-text-primary'
              }`}
            >
              {lbl}
              {key === 'inbox' && needsNow > 0 && (
                <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-error dark:bg-status-stuck-solid text-white text-[10.5px] leading-[18px] text-center tabular-nums"
                  aria-label={t('{n} à traiter maintenant', { n: needsNow })}>
                  {needsNow}
                </span>
              )}
            </button>
          ))}
          </SwipeStrip>
        </nav>

        {/* The numbers that matter, on a phone: STUCK first, each one a way in. */}
        {(tab === 'inbox' || tab === 'pipeline' || tab === 'conversations') && <NumbersStrip range={range} onGo={goStrip} />}

        {tab === 'inbox' && <InboxTab onOpenChat={openChat} range={range} onShowAll={showAll} />}
        {tab === 'pipeline' && (<>
          <PipelineTab onOpenChat={openChat} range={range} onShowAll={showAll}
            stuckOnly={sub === 'stuck'} onStuckOnly={(on) => go('pipeline', on ? { sub: 'stuck' } : {})} />
        </>)}
        {tab === 'conversations' && <ConversationsTab chatId={chatParam} onSelectChat={selectChat} onCloseChat={closeChat} range={range} onShowAll={showAll} />}
        {tab === 'live' && <LiveTab onOpenChat={openChat} onOpenInbox={() => go('inbox')} />}
        {tab === 'approvals' && <ApprovalsTab onOpenChat={openChat} />}
        {tab === 'shadow' && <EditShadowTab onOpenChat={openChat} />}
        {tab === 'brain' && (<>
          <SubSwitch
            value={sub === 'simulator' ? 'simulator' : 'rules'}
            options={[['rules', t('Règles et réponses')], ['simulator', t('Simulateur')]]}
            onChange={(v) => go('brain', { sub: v })}
          />
          {sub === 'simulator' ? <SimulatorTab /> : <BrainTab />}
        </>)}
        {tab === 'automations' && (<>
          <SubSwitch
            value={sub === 'sales' || sub === 'process' ? sub : 'followups'}
            options={[['followups', t('Relances')], ['sales', t('Ventes')], ['process', t('Processus de l’agent')]]}
            onChange={(v) => go('automations', { sub: v })}
          />
          {sub === 'sales' ? <SalesTab /> : sub === 'process' ? <ProcessTab /> : (<>
            {/* ⚠️ RELANCES IN ONE PLACE (owner plan, 2026-10-01): the agent's
                rules AND the manual reminder list (the old /admin/follow-up
                page, which now redirects here). Two tools, one door. */}
            <div>
              <SubSwitch
                value={params.get('view') === 'manual' ? 'manual' : 'rules'}
                options={[['rules', t('Règles de l’agent')], ['manual', t('Liste à relancer')]]}
                onChange={(v) => go('automations', { sub: 'followups', view: v })}
              />
            </div>
            {params.get('view') === 'manual' ? <ManualReminders initialFilter={params.get('filter')} /> : <FollowUpsTab />}
          </>)}
        </>)}
        {tab === 'settings' && (<>

        {/* The switch that decides whether customers hear from the agent at all. */}
        <MasterSwitch />


        <div className="grid grid-cols-3 gap-3">
          <StatTile label={t('Connectés')} value={relay.online ? connected : '—'} tone={connected > 0 ? 'ok' : 'neutral'} />
          <StatTile label={t('À associer')} value={relay.online ? waiting : '—'} tone={waiting > 0 ? 'warn' : 'neutral'} />
          <StatTile label={t('Total')} value={numbers.length} tone="neutral" />
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-800 mb-1">{t('Ajouter un numéro')}</h2>
          <p className="text-[11.5px] text-gray-500 mb-3">
            {t('Donnez-lui un nom, puis associez-le en scannant le QR code ou avec un code à taper.')}
          </p>
          <label htmlFor="new-label" className="block text-[11px] font-semibold text-gray-500 mb-1">{t('Nom')}</label>
          <div className="flex gap-2 items-start">
            <input
              id="new-label" className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder={t('Ex. Numéro de secours')} maxLength={60}
              onKeyDown={(e) => { if (e.key === 'Enter' && label.trim()) addMut.mutate(); }}
            />
            <Button size="sm" onClick={() => addMut.mutate()} loading={addMut.isPending} disabled={!label.trim()}>
              <span className="inline-flex items-center gap-1.5"><PlusIcon className="w-4 h-4" />{t('Ajouter')}</span>
            </Button>
          </div>
          {addMut.isError && <p className="text-[11.5px] text-red-600 mt-2" role="alert">{(addMut.error as Error).message}</p>}
          {!relay.online && (
            <p className="text-[11.5px] text-amber-700 mt-2 flex items-start gap-1.5">
              <AlertIcon className="w-4 h-4 mt-px shrink-0" />
              <span>{t('Le serveur est hors ligne : le numéro sera créé mais l’association ne démarrera qu’à son retour.')}</span>
            </p>
          )}
        </div>

        <OrphanCard orphans={orphans} />

        <div className="space-y-2.5">
          {isLoading && <><SkeletonCard /><SkeletonCard /></>}
          {isError && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-2.5" role="alert">
              <AlertIcon className="w-[18px] h-[18px] text-red-700 mt-px" />
              <div>
                <p className="text-[12.5px] font-semibold text-red-800">{t('Impossible de charger les numéros.')}</p>
                <p className="text-[11.5px] text-red-700 mt-0.5">{(error as Error).message}</p>
              </div>
            </div>
          )}
          {!isLoading && !isError && numbers.length === 0 && (
            <div className="bg-white rounded-xl border border-dashed border-gray-200 p-10 text-center">
              <div className="w-11 h-11 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-3">
                <PhoneIcon className="w-5 h-5 text-gray-300" />
              </div>
              <p className="text-[13px] font-medium text-gray-700">{t('Aucun numéro pour le moment.')}</p>
              <p className="text-[11.5px] text-gray-500 mt-1 max-w-sm mx-auto leading-snug">
                {t('Ajoutez-en un ci-dessus pour que l’assistant puisse répondre aux clients. Utilisez un numéro dédié, pas votre numéro principal.')}
              </p>
            </div>
          )}
          {numbers.map((n) => <NumberCard key={n.id} number={n} relayOnline={relay.online} />)}
        </div>

        {/* Who the connected lines above are allowed to talk to. Placed right
            below them on purpose: it's about the same lines, not a separate
            concern. */}
        <TestNumbersCard />

        {/* Payment proposals, open loops and client promises moved to the Inbox
            (money waiting on a decision is a person's job, and the Inbox is
            where a person looks); the orders being formed moved to Live. */}

        {/* Who picks up when the assistant CANNOT answer. Directly under the
            card that decides what it may send: those two questions — what goes
            out on its own, and who takes over when it stops — are the same
            decision seen from both ends. */}
        <div className="mt-4">
          <DepartmentContactsCard />
        </div>

        <div className="mt-4">
          <EngineCard />
        </div>
        </>)}
      </div>
    </AdminLayout>
  );
}
